import type { ReactNode } from 'react';

interface SegmentedControlOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  /** Empty string means no segment is selected. */
  value: string;
  onChange: (value: string) => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  hideLabelsOnMobile?: boolean;
}

const SegmentedControl = ({
  options,
  value,
  onChange,
  className = '',
  size = 'medium',
  hideLabelsOnMobile = false
}: SegmentedControlProps) => {
  const sizeClasses = {
    small: {
      container: 'p-0.5 ~gap-0.5/1',
      button: '~px-2/3 ~py-0.5/1 ~text-[0.625rem]/xs',
      borderRadius: 'rounded-sm'
    },
    medium: {
      container: 'p-0.5 ~gap-1/2',
      button: '~px-3/4 ~py-1/1.5 ~text-xs/sm',
      borderRadius: 'rounded-md'
    },
    large: {
      container: 'p-1 ~gap-2/3',
      button: '~px-4/5 ~py-1.5/2 ~text-sm/base',
      borderRadius: 'rounded-lg'
    }
  };

  const currentSize = sizeClasses[size];

  const handleOptionClick = (optionValue: string, disabled?: boolean) => {
    if (!disabled && optionValue !== value) {
      onChange(optionValue);
    }
  };

  return (
    <div
      className={`inline-flex items-center ${currentSize.container} rounded-md border border-secondary-400/60 bg-primary-100 dark:bg-secondary-dark-100 dark:border-secondary-dark-400 shadow-sm ${className}`}
      role='tablist'
      aria-label='Segmented control'>
      {options.map((option, index) => {
        const isActive = option.value === value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        return (
          <button
            key={option.value}
            type='button'
            role='tab'
            aria-selected={isActive}
            aria-controls={`panel-${option.value}`}
            disabled={option.disabled}
            onClick={() => handleOptionClick(option.value, option.disabled)}
            className={`
              ${currentSize.button}
              ${currentSize.borderRadius}
              transition-all duration-200 ease-in-out
              font-medium
              ${
                isActive
                  ? 'bg-white dark:bg-primary-dark-200 text-primary-900 dark:text-primary-dark-600 shadow-sm'
                  : 'text-gray-600 dark:text-secondary-dark-900 hover:text-gray-800 dark:hover:text-primary-dark-900'
              }
              ${
                option.disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }
              ${!isFirst ? '' : ''}
              ${!isLast ? '' : ''}
              focus:outline-none focus:ring-2 focus:ring-primary-500/50 dark:focus:ring-primary-dark-500/50
            `}>
            <span className='flex items-center ~gap-1/1.5'>
              {option.icon && <span className='shrink-0'>{option.icon}</span>}
              <span className={hideLabelsOnMobile ? 'hidden sm:inline' : ''}>
                {option.label}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
